import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { UserStoreService } from '@hp-core/services/user-store.service';
import { IHandoffSession } from '../_types/auth.interface';

/** Query param con el que el dashboard v1 hace el handoff de sesión. */
export const HANDOFF_SESSION_PARAM = 'hp_session';

/**
 * `APP_INITIALIZER` que intercepta un salto desde el dashboard v1: si la URL trae
 * el param `hp_session` (JSON `{ token, role, username, client }` en base64), lo
 * decodifica y persiste la sesión ANTES de que corran los guards, de modo que el
 * usuario aterriza directo en la vista pedida sin rehacer login.
 *
 * Corre antes del router (por eso es un APP_INITIALIZER y devuelve una Promise que
 * el bootstrap espera). Siempre limpia el param de la URL — incluso si el token era
 * inválido — para no dejar el JWT en historial/logs/`Referer`. Nunca lanza: ante
 * cualquier error, el flujo normal (guard → login) decide.
 *
 * En SSR es no-op (no hay `window` ni localForage).
 */
export function sessionHandoffInitializerFactory(userStore: UserStoreService, platformId: object): () => Promise<void> {
  return async () => {
    if (!isPlatformBrowser(platformId)) {
      return;
    }

    const url = new URL(window.location.href);
    const raw = url.searchParams.get(HANDOFF_SESSION_PARAM);
    if (!raw) {
      return;
    }

    try {
      const payload = decodeHandoffPayload(raw);
      if (payload) {
        await firstValueFrom(userStore.hydrateFromHandoff(payload));
      }
    } catch {
      // Handoff inválido: se ignora y el guard mandará a login.
    } finally {
      stripHandoffParam(url);
    }
  };
}

function decodeHandoffPayload(raw: string): IHandoffSession | null {
  try {
    const json = decodeURIComponent(escape(atob(decodeURIComponent(raw))));
    return JSON.parse(json) as IHandoffSession;
  } catch {
    return null;
  }
}

/** Quita solo `hp_session`, preservando path, hash y demás query params de la vista destino. */
function stripHandoffParam(url: URL): void {
  url.searchParams.delete(HANDOFF_SESSION_PARAM);
  const cleaned = url.pathname + url.search + url.hash;
  window.history.replaceState(window.history.state, '', cleaned);
}
